/**
 * Role-based access map.
 *
 * Source of truth for which staff role can visit which admin pages.
 * Роль даёт базовый набор страниц; сверху добавляются персональные
 * страницы из staff.extraRoutes — additive, как canEdit для полей заказа:
 * добавить страницу человеку можно, отобрать ролевую через них нельзя.
 * Нужно для сотрудников, которым доступ шире роли, а роль менять нельзя
 * (на sales завязаны смены, зарплаты, привязка транзакций по имени).
 *
 * Старый per-staff массив `routes` тут ни при чём — он легаси, хранится
 * ради Metrics.tsx и как право не читается. Бэкенд-двойник этого файла —
 * backend/src/utils/staffAccess.ts.
 */

export const ALL_ROUTES = [
  "/",
  "/pulse",
  "/sales",
  "/refunds",
  "/transactions",
  "/editor",
  "/log",
  "/promo-codes",
  "/storage",
  "/team",
] as const;

/** Подписи страниц — для галочек доступа на странице «Команда».
 *  Совпадают с пунктами сайдбара. */
export const ROUTE_LABELS: Record<string, string> = {
  "/": "Главная",
  "/pulse": "Пульс",
  "/sales": "Продажи",
  "/refunds": "Возвраты",
  "/transactions": "Транзакции",
  "/editor": "Редактура",
  "/log": "Логистика",
  "/promo-codes": "Промокоды",
  "/storage": "Хранилище",
  "/team": "Команда",
};

// Страница /transactions доступна всем сотрудникам КРОМЕ sales.
export const ROLE_ROUTES: Record<string, readonly string[]> = {
  admin: ALL_ROUTES,
  operations: ALL_ROUTES.filter((r) => r !== "/team" && r !== "/" && r !== "/promo-codes" && r !== "/pulse"),
  editor: ["/editor", "/storage", "/transactions"],
  headeditor: ["/editor", "/storage", "/transactions"],
  marketing: ["/pulse", "/promo-codes", "/transactions"],
  // "/sales" первым: defaultRouteForRole берёт ROLE_ROUTES[role][0], продажник
  // должен приземляться на продажи, а не на возвраты.
  sales: ["/sales", "/refunds"],
  log: ["/log", "/transactions"],
};

/** Кто может оформлять и удалять возвраты — продажники, админы, operations.
 *  Отдельно от canEditField('status'): у sales права на статус нет вообще,
 *  а возврат делать он должен. Бэкенд-двойник — REFUND_ROLES в
 *  backend/src/routes/refunds.ts. */
export const REFUND_ROLES = ["admin", "sales", "operations"];

export function canMakeRefund(role: string | undefined): boolean {
  return !!role && REFUND_ROLES.includes(role);
}

/** Кто может архивировать заказ и возвращать его в работу. Отдельно от
 *  canEditField('status'): редактору нужен именно архив (убрать из работы
 *  брошенную книгу), а полный дропдаун статусов ему давать нельзя — там
 *  «Отправлено» и «Доставлено», это логистика. Возврат из архива всегда
 *  ставит «Заполнил»: восстанавливать прежний статус неоткуда, а книга
 *  после архива всё равно идёт заново через редактуру. */
export const ARCHIVE_ROLES = ["admin", "operations", "editor", "headeditor"];

/** /editor/:username (per-book edit page) — both editor- and operations-tier
 *  roles need it; gated separately from the role→routes map. */
export const EDITOR_DETAIL_ROLES = ["admin", "editor", "headeditor", "operations"];

/** Карточка книги открывается тем же, кому открыт список редактуры:
 *  роль из EDITOR_DETAIL_ROLES либо персонально выданный /editor. */
export function canAccessEditorDetail(
  role: string | undefined,
  extraRoutes: readonly string[] = []
): boolean {
  if (!role) return false;
  return EDITOR_DETAIL_ROLES.includes(role) || extraRoutes.includes("/editor");
}

/** Персональная выдача страницы = права admin-уровня на ней: страницу дают
 *  ровно затем, чтобы человек видел не только своё. Бэк считает так же
 *  (backend/src/utils/staffAccess.ts). */
export function hasElevatedRoute(
  extraRoutes: readonly string[] | undefined,
  route: string
): boolean {
  return !!extraRoutes?.includes(route);
}

/** Полный набор страниц сотрудника: ролевые + персональные добавки. */
export function allowedRoutesFor(
  role: string | undefined,
  extraRoutes: readonly string[] = []
): readonly string[] {
  if (!role) return [];
  const base = ROLE_ROUTES[role] || [];
  // Порядок — как в ALL_ROUTES, чтобы сайдбар и дефолтная страница
  // не зависели от порядка галочек в «Команде».
  return ALL_ROUTES.filter((r) => base.includes(r) || extraRoutes.includes(r));
}

/** Match a concrete path (e.g. "/storage/2026-05-11/john") against the
 *  list of allowed paths for the staff member. "/storage" и "/log" —
 *  префиксы: доступ к ним открывает и вложенные страницы. */
export function canAccessRoute(
  role: string | undefined,
  path: string,
  extraRoutes: readonly string[] = []
): boolean {
  return allowedRoutesFor(role, extraRoutes).some((p) => {
    if (p === path) return true;
    if (p === "/storage" && path.startsWith("/storage/")) return true;
    if (p === "/log" && path.startsWith("/log/")) return true;
    return false;
  });
}

/** First page a freshly-logged-in staff should land on. Ролевая страница
 *  приоритетнее персональных: продажник с добавками всё равно приземляется
 *  на «Продажи». */
export function defaultRouteForRole(
  role: string | undefined,
  extraRoutes: readonly string[] = []
): string {
  if (!role) return "/not-authorized";
  return (
    ROLE_ROUTES[role]?.[0] ||
    allowedRoutesFor(role, extraRoutes)[0] ||
    "/not-authorized"
  );
}

/**
 * Per-field view/edit permissions for the User.tsx popup, by role.
 *
 * База — роль; сверху добавляются персональные поля из staff.canEdit
 * (additive-only: расширяют ролевые права, отобрать поле через них нельзя).
 * All roles can VIEW the full order; what differs is what each role can edit.
 *
 *  - admin / operations  — full control.
 *  - sales               — runs the order start-to-end except editor/logist
 *                          assignments and the express type (set in editing).
 *  - editor              — own assignment, express type, status (mark done),
 *                          general comment.
 *  - log                 — everything delivery-related + status (mark shipped).
 *  - marketing           — influencer flag + general comment.
 */

const ORDER_FIELDS = [
  "status",
  // Общая сумма заказа. Роль admin/operations получает её вместе со всеми
  // полями, остальным выдаётся персонально галочкой на странице «Команда».
  "fullAmount",
  "express",
  "platform",
  "isInfluencer",
  "manager",
  "salesMan", // legacy alias of manager — kept so old payloads stay editable
  "editor",
  "logist",
  "logMethod",
  "country",
  "city",
  "address",
  "recipient",
  "courierNumber",
  "cdekNumber",
  "whatsapp",
  "telegram",
  "comment",
  "commentLogis",
] as const;

const ALL_FIELDS: readonly string[] = ORDER_FIELDS;

/** Поля карточки заказа, которые можно выдать сотруднику персонально
 *  (staff.canEdit), с подписями — для страницы «Команда». salesMan
 *  не показываем: это легаси-алиас manager. */
export const ORDER_FIELD_LABELS: { key: string; label: string }[] = [
  { key: "status", label: "Статус" },
  { key: "fullAmount", label: "Общая сумма" },
  { key: "express", label: "Экспресс" },
  { key: "platform", label: "Площадка" },
  { key: "isInfluencer", label: "Инфлюенсер" },
  { key: "manager", label: "Менеджер" },
  { key: "editor", label: "Редактор" },
  { key: "logist", label: "Логист" },
  { key: "logMethod", label: "Способ доставки" },
  { key: "country", label: "Страна" },
  { key: "city", label: "Город" },
  { key: "address", label: "Адрес" },
  { key: "recipient", label: "Получатель" },
  { key: "courierNumber", label: "Номер курьера" },
  { key: "cdekNumber", label: "Номер СДЭК" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "telegram", label: "Telegram" },
  { key: "comment", label: "Комментарий" },
  { key: "commentLogis", label: "Комментарий логистики" },
];

export const ROLE_FIELD_PERMISSIONS: Record<
  string,
  { canView: readonly string[]; canEdit: readonly string[] }
> = {
  admin: { canView: ALL_FIELDS, canEdit: ALL_FIELDS },
  operations: { canView: ALL_FIELDS, canEdit: ALL_FIELDS },
  sales: {
    canView: ALL_FIELDS,
    canEdit: [
      "platform",
      "isInfluencer",
      "country",
      "city",
      "address",
      "recipient",
      "whatsapp",
      "telegram",
      "comment",
    ],
  },
  editor: {
    canView: ALL_FIELDS,
    canEdit: ["comment"],
  },
  // Как editor, плюс право менять статус заказа юзера.
  headeditor: {
    canView: ALL_FIELDS,
    canEdit: ["comment", "status"],
  },
  log: {
    canView: ALL_FIELDS,
    canEdit: [
      "logist",
      "country",
      "city",
      "address",
      "recipient",
      "logMethod",
      "courierNumber",
      "cdekNumber",
      "whatsapp",
      "telegram",
      "commentLogis",
    ],
  },
  marketing: {
    canView: ALL_FIELDS,
    canEdit: ["isInfluencer", "comment"],
  },
};

export function canViewField(role: string | undefined, field: string): boolean {
  if (!role) return false;
  return !!ROLE_FIELD_PERMISSIONS[role]?.canView.includes(field);
}

export function canEditField(
  role: string | undefined,
  field: string,
  staffCanEdit?: readonly string[]
): boolean {
  if (!role) return false;
  if (ROLE_FIELD_PERMISSIONS[role]?.canEdit.includes(field)) return true;
  return !!staffCanEdit?.includes(field);
}
